import type { AnchorHTMLAttributes, DetailedHTMLFactory } from 'react';
import React, { createElement } from 'react';

import omit from 'lodash.omit';

import { Text } from '../Text';
import { cnLink } from './Link.cn';
import type { ILinkProps, LinkElement } from './Link.types';

import './Link.css';

type ReactLinkElement = DetailedHTMLFactory<AnchorHTMLAttributes<LinkElement>, LinkElement>;

/**
 * Компонент для создания ссылок.
 * @param {ILinkProps} props
 */
export const Link = <TElement extends React.ElementType = 'a'>(props: ILinkProps<TElement>) => {
    const {
        as: AsComponent,
        children,
        controlRef,
        isDisabled,
        innerRef,
        role,
        view = 'link',
        iconLeft,
        iconRight,
        pseudo: _pseudo,
        size,
        isOneLine,
        overflowTooltipProps,
        ...restProps
    } = props;

    const Component =
        AsComponent || ((restProps.href ? 'a' : ('span' as unknown)) as ReactLinkElement);
    let rel = restProps.rel;

    if (
        restProps.target === '_blank' &&
        rel !== undefined &&
        (rel as string).indexOf('noopener') === -1
    ) {
        // Пользовательский атрибут имеет больший приоритет.
        rel = `${rel} noopener`;
    }

    // PERF: Не используем TSX, чтобы избежать лишнего клонирования props.
    return createElement(
        Component,
        Object.assign(omit(restProps, ['hasVisited', 'isOneLine', 'overflowTooltipProps']), {
            'aria-disabled': isDisabled,
            'disabled': isDisabled,
            'className': cnLink(
                {
                    view,
                    size,
                    hasVisited: 'hasVisited' in restProps && restProps.hasVisited,
                    isOneLine,
                },
                [restProps.className],
            ),
            'ref': innerRef || controlRef,
            rel,
            role,
            'tabIndex': isDisabled ? -1 : restProps.tabIndex,
        }),
        <>
            {iconLeft && iconLeft(cnLink('Icon', { left: true }))}
            <Text
                as="span"
                maxVisibleLines={isOneLine ? 1 : undefined}
                overflowTooltipProps={overflowTooltipProps}
                className={cnLink('Content')}
            >
                {children}
            </Text>
            {iconRight && iconRight(cnLink('Icon', { right: true }))}
        </>,
    );
};

Link.displayName = cnLink();
